import * as React from 'react';
import { useEffect } from 'react';

import { Card, Cascader, Space, Typography, InputNumber, Switch, Input, Popover } from 'antd';

import { ActionConfig, BudgetUnit, ActionType, AdSource, HierarchicalActionTypes, DictionaryItem, TelegramUser } from '../../types';
import { scopeNoneLabel } from '../../utils/helpers';
import { RecipientsPicker } from '../telegram/RecipientsPicker';

interface ActionsPanelProps {
  value: ActionConfig;
  onChange: (v: ActionConfig) => void;
  scopeOne: 'campaigns' | 'adGroups' | 'ads';
  adSource?: AdSource;
  actionTypes?: HierarchicalActionTypes;
  telegramUsers?: TelegramUser[];
}

export function ActionsPanel({ value, onChange, scopeOne, adSource = 'yandex_direct', actionTypes, telegramUsers = [] }: ActionsPanelProps) {
  useEffect(() => {
    const shouldShowBudgetActions = adSource !== 'yandex_direct' || scopeOne === 'campaigns';

    if (!shouldShowBudgetActions && actionTypes && !isNaN(Number(value.actionType))) {
      const sourceId = adSource === 'yandex_direct' ? '2' : '9';
      const sourceActions = actionTypes[sourceId];

      if (sourceActions) {
        const increaseAction = sourceActions.increase?.find(action => action.id.toString() === value.actionType);
        const decreaseAction = sourceActions.decrease?.find(action => action.id.toString() === value.actionType);

        if ((increaseAction || decreaseAction) && (value.budgetAction !== 'none' || value.actionType !== 'none')) {
          onChange({ ...value, budgetAction: 'none', actionType: 'none' });
        }
      }
    }

    // Сбрасываем действие "Отключаем" для групп объявлений в Яндекс Директ
    // так как Yandex API не поддерживает отключение групп объявлений
    const isYandexAdGroups = adSource === 'yandex_direct' && scopeOne === 'adGroups';
    const isDisableAction = value.actionType === '4' || value.actionType === 'disable' || value.budgetAction === 'disable';

    if (isYandexAdGroups && isDisableAction) {
      onChange({ ...value, budgetAction: 'none', actionType: 'none' });
    }
  }, [scopeOne, adSource, actionTypes, value.actionType, value.budgetAction, onChange]);

  // Определяем, нужно ли показывать поля для суммы и единицы измерения
  const showAmount = (() => {
    if (!value.actionType) return false;

    // Если это API ID (число), проверяем по ID
    if (actionTypes && !isNaN(Number(value.actionType))) {
      const sourceId = adSource === 'yandex_direct' ? '2' : '9';
      const sourceActions = actionTypes[sourceId];

      if (sourceActions) {
        // Проверяем в other действиях (ID: 1, 4 - не показываем поля)
        const otherAction = sourceActions.other?.find(action => action.id.toString() === value.actionType);
        if (otherAction && (otherAction.id === 1 || otherAction.id === 4)) {
          return false; // "Ничего не меняем" или "Отключаем"
        }

        // Проверяем в increase/decrease действиях (показываем поля)
        const increaseAction = sourceActions.increase?.find(action => action.id.toString() === value.actionType);
        const decreaseAction = sourceActions.decrease?.find(action => action.id.toString() === value.actionType);
        if (increaseAction || decreaseAction) {
          return true;
        }
      }
    }

    // Fallback для старых значений
    return value.actionType !== 'none' && value.actionType !== 'disable';
  })();
  const noneLabel = scopeNoneLabel(scopeOne);

  // Получаем доступные действия из API данных
  const getAvailableActions = () => {
    if (!actionTypes) {
      // Fallback на статичные данные если API не загружен
      return [
        { value: 'none', label: noneLabel },
        { value: 'disable', label: 'Отключаем' }
      ];
    }

    const sourceId = adSource === 'yandex_direct' ? '2' : '9';
    const sourceActions = actionTypes[sourceId];

    if (!sourceActions) {
      return [
        { value: 'none', label: noneLabel },
        { value: 'disable', label: 'Отключаем' }
      ];
    }

    const actions = [];

    // Добавляем "other" действия (Ничего не меняем, Отключаем)
    if (sourceActions.other && sourceActions.other.length > 0) {
      sourceActions.other.forEach((action: DictionaryItem) => {
        // Для групп объявлений в Яндекс Директ убираем действие "Отключаем" (ID: 4)
        // так как Yandex API не поддерживает отключение групп объявлений
        const isYandexAdGroups = adSource === 'yandex_direct' && scopeOne === 'adGroups';
        const isDisableAction = action.id === 4;

        if (isYandexAdGroups && isDisableAction) {
          return; // Пропускаем действие "Отключаем" для групп Яндекс Директ
        }

        actions.push({
          value: action.id.toString(),
          label: action.name
        });
      });
    }

    const shouldShowBudgetActions = adSource !== 'yandex_direct' || scopeOne === 'campaigns';

    if (shouldShowBudgetActions && sourceActions.increase && sourceActions.increase.length > 0) {
      const increaseChildren = sourceActions.increase.map((action: DictionaryItem) => ({
        value: action.id.toString(),
        label: action.name
      }));

      actions.push({
        value: 'increase',
        label: 'Увеличиваем',
        children: increaseChildren
      });
    }

    // Добавляем "decrease" действия
    if (shouldShowBudgetActions && sourceActions.decrease && sourceActions.decrease.length > 0) {
      const decreaseChildren = sourceActions.decrease.map((action: DictionaryItem) => ({
        value: action.id.toString(),
        label: action.name
      }));

      actions.push({
        value: 'decrease',
        label: 'Уменьшаем',
        children: decreaseChildren
      });
    }

    return actions;
  };

  const actionOptions = getAvailableActions();

  const handleActionChange = (selectedValues: (string | number)[]) => {
    if (!selectedValues || selectedValues.length === 0) {
      onChange({ ...value, budgetAction: 'none', actionType: 'none' });
      return;
    }

    const lastValue = selectedValues[selectedValues.length - 1] as string;

    // Проверяем, является ли выбранное значение ID из API
    if (actionTypes) {
      const sourceId = adSource === 'yandex_direct' ? '2' : '9';
      const sourceActions = actionTypes[sourceId];

      if (sourceActions) {
        // Проверяем в other действиях
        const otherAction = sourceActions.other?.find(action => action.id.toString() === lastValue);

        if (otherAction) {
          if (otherAction.id === 1) { // "Ничего не меняем"
            onChange({ ...value, budgetAction: 'none', actionType: lastValue });
          } else if (otherAction.id === 4) { // "Отключаем"
            onChange({ ...value, budgetAction: 'disable', actionType: lastValue });
          }
          return;
        }

        // Проверяем в increase действиях
        const increaseAction = sourceActions.increase?.find(action => action.id.toString() === lastValue);
        if (increaseAction) {
          onChange({ ...value, budgetAction: 'increase', actionType: lastValue as ActionType });
          return;
        }

        // Проверяем в decrease действиях
        const decreaseAction = sourceActions.decrease?.find(action => action.id.toString() === lastValue);
        if (decreaseAction) {
          onChange({ ...value, budgetAction: 'decrease', actionType: lastValue as ActionType });
          return;
        }
      }
    }

    // Fallback для старых значений
    if (lastValue === 'none') {
      onChange({ ...value, budgetAction: 'none', actionType: 'none' });
    } else if (lastValue === 'disable') {
      onChange({ ...value, budgetAction: 'disable', actionType: 'disable' });
    } else if (lastValue.startsWith('increase_')) {
      onChange({ ...value, budgetAction: 'increase', actionType: lastValue as ActionType });
    } else if (lastValue.startsWith('decrease_')) {
      onChange({ ...value, budgetAction: 'decrease', actionType: lastValue as ActionType });
    }
  };

  const getCurrentValue = (): (string | number)[] => {
    // Если это API ID (число), ищем в структуре данных
    if (actionTypes && !isNaN(Number(value.actionType))) {
      const sourceId = adSource === 'yandex_direct' ? '2' : '9';
      const sourceActions = actionTypes[sourceId];

      if (sourceActions) {
        // Проверяем в other действиях
        const otherAction = sourceActions.other?.find(action => action.id.toString() === value.actionType);
        if (otherAction) {
          return [value.actionType!];
        }

        // Проверяем в increase действиях
        const increaseAction = sourceActions.increase?.find(action => action.id.toString() === value.actionType);
        if (increaseAction) {
          return ['increase', value.actionType!];
        }

        // Проверяем в decrease действиях
        const decreaseAction = sourceActions.decrease?.find(action => action.id.toString() === value.actionType);
        if (decreaseAction) {
          return ['decrease', value.actionType!];
        }
      }
    }

    // Для старых значений или когда API не загружен
    if (value.actionType === 'none' || !value.actionType) {
      // Ищем в API данных "Ничего не меняем" (ID: 1)
      if (actionTypes) {
        const sourceId = adSource === 'yandex_direct' ? '2' : '9';
        const sourceActions = actionTypes[sourceId];
        const nothingAction = sourceActions?.other?.find(action => action.id === 1);
        if (nothingAction) {
          return [nothingAction.id.toString()];
        }
      }
      return ['none'];
    }

    if (value.actionType === 'disable') {
      // Ищем в API данных "Отключаем" (ID: 4)
      if (actionTypes) {
        const sourceId = adSource === 'yandex_direct' ? '2' : '9';
        const sourceActions = actionTypes[sourceId];
        const disableAction = sourceActions?.other?.find(action => action.id === 4);
        if (disableAction) {
          return [disableAction.id.toString()];
        }
      }
      return ['disable'];
    }

    // Fallback для старых значений
    if (typeof value.actionType === 'string' && value.actionType.startsWith('increase_')) {
      return ['increase', value.actionType];
    }
    if (typeof value.actionType === 'string' && value.actionType.startsWith('decrease_')) {
      return ['decrease', value.actionType];
    }

    return [];
  };

  const dynamicParametersContent = (
    <div style={{ maxWidth: 300, padding: '8px 0' }}>
      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
        Доступные динамические параметры:
      </Typography.Text>
      <div style={{ fontSize: 12, lineHeight: 1.6 }}>
        <div><code>{'{rule_name}'}</code> - название правила</div>
        <div><code>{'{ad_source}'}</code> - источник рекламы</div>
        <div><code>{'{account_name}'}</code> - рекламный кабинет</div>
        <div><code>{'{object_type}'}</code> - тип объекта (кампания/группа/объявление)</div>
        <div><code>{'{condition}'}</code> - выполненное условие</div>
        <div><code>{'{object_numbers}'}</code> - номера объектов</div>
        <div><code>{'{action_taken}'}</code> - выполненное действие</div>
        <div><code>{'{date_time}'}</code> - дата и время срабатывания</div>
        <div><code>{'{object_names}'}</code> - названия объектов</div>
      </div>
    </div>
  );

  return (
    <Card size="small" style={{ border: 'none', boxShadow: 'none' }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Typography.Text strong>Что делаем?</Typography.Text>

        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Cascader
            options={actionOptions}
            value={getCurrentValue()}
            onChange={handleActionChange}
            style={{ minWidth: 200, width: 'auto' }}
            placeholder="Выберите действие"
            expandTrigger="hover"
            displayRender={(labels) => labels.join(' → ')}
            dropdownStyle={{ minWidth: 300, maxHeight: 'none', height: 'auto' }}
            popupClassName="adaptive-cascader-popup"
          />
          {(value.actionType === 'disable' || (actionTypes && value.actionType === '4')) && (
            <Typography.Text type="secondary" style={{ fontSize: 14 }}>
              {scopeOne === 'campaigns' ? 'Кампанию' : scopeOne === 'adGroups' ? 'Группу объявлений' : 'Объявление'}
            </Typography.Text>
          )}
          {showAmount && (
            <>
              <Typography.Text type="secondary">на</Typography.Text>
              <InputNumber
                value={value.budgetAmount}
                onChange={(num) => onChange({ ...value, budgetAmount: Number(num || 0) })}
                style={{ width: 80 }}
                min={1}
                placeholder="1"
              />
              <Cascader
                options={[
                  { value: 'rub', label: 'Руб.' },
                  { value: 'percent', label: '%' }
                ]}
                value={[value.budgetUnit]}
                onChange={(vals) => onChange({ ...value, budgetUnit: (vals[0] || 'rub') as BudgetUnit })}
                style={{ width: 'auto', minWidth: 80 }}
                dropdownStyle={{ minWidth: 100, maxHeight: 'none', height: 'auto' }}
                popupClassName="adaptive-cascader-popup"
              />
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch
              checked={value.sendTelegram}
              onChange={(checked) => onChange({ ...value, sendTelegram: checked })}
            />
            <Typography.Text>Отправить уведомление в Telegram после срабатывания правила</Typography.Text>
          </div>
          {value.sendTelegram && (
            <Space direction="vertical" style={{ width: '100%', maxWidth: 640 }}>
              <div style={{ marginBottom: 8 }}>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Можете ввести любой текст и вставить{' '}
                  <Popover
                    content={dynamicParametersContent}
                    title={null}
                    trigger="click"
                    placement="topLeft"
                  >
                    <Typography.Link style={{ fontSize: 13 }}>
                      динамические параметры
                    </Typography.Link>
                  </Popover>
                </Typography.Text>
              </div>
              <Input.TextArea
                placeholder="Введите текст сообщения..."
                value={value.tgMessage}
                onChange={(e) => onChange({ ...value, tgMessage: e.target.value })}
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
              <RecipientsPicker
                value={value.tgRecipients}
                onChange={(users) => onChange({ ...value, tgRecipients: users })}
                status={value.sendTelegram && value.tgRecipients.length === 0 ? 'error' : undefined}
                options={telegramUsers}
              />
            </Space>
          )}
        </div>
      </Space>
    </Card>
  );
}
